// src/hooks/useTier.ts
//
// Thin selector over AuthContext for the merchant's pricing tier. The tier comes
// from the merchant profile (GET /merchants/me → subscription_tier), so it stays
// DB-fresh on every mount/login and after a plan change (refetch the profile).
//
// UI gating only — the backend enforces entitlement on every gated route.

import { useAuth } from "@/contexts/AuthContext";
import { hasFeature as tierHasFeature, type Feature, type Tier } from "@/lib/tier";

export interface UseTierResult {
  /** The merchant's current tier, or null if unknown/not loaded. */
  tier: Tier | null;
  /** Billing cycle, or null if unknown. */
  billingCycle: "monthly" | "annual" | null;
  /** True while the auth/profile is still loading. */
  loading: boolean;
  /** Whether the current tier is entitled to `feature` (fail-closed on unknown). */
  hasFeature: (feature: Feature) => boolean;
}

function asTier(value: unknown): Tier | null {
  return value === "essentials" || value === "performance" || value === "power"
    ? value
    : null;
}

export function useTier(): UseTierResult {
  const { merchant, loading } = useAuth();
  const tier = asTier(merchant?.subscription_tier);
  const billingCycle =
    merchant?.billing_cycle === "annual" || merchant?.billing_cycle === "monthly"
      ? merchant.billing_cycle
      : null;

  return {
    tier,
    billingCycle,
    loading,
    hasFeature: (feature: Feature) => tierHasFeature(tier, feature),
  };
}
